import { useState, useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { Star } from "@/components/landing/Logo";
import { StoreBadges } from "@/components/landing/StoreBadges";
import { useLang } from "@/i18n/LanguageContext";
import T from "@/data/miraPlusContent";
import { Shirt, Wand2, Sparkles, ScanSearch, CalendarDays, ListChecks, Check, Minus, Heart, ShieldCheck, XCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FEATURE_ICONS = [Shirt, Sparkles, Wand2, Heart, ScanSearch, CalendarDays];

const BeforeAfter = ({ x }) => {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative select-none overflow-hidden rounded-[1.8rem] border-2 shadow-2xl" style={{ borderColor: "var(--gold)", aspectRatio: "848/1264" }} data-testid="plus-mirror-slider">
      <img src="/img/mira-plus-before.jpg" alt={x.mirrorBefore} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img src="/img/mira-plus-after.jpg" alt={x.mirrorAfter} className="h-full w-full object-cover" draggable={false} />
      </div>
      <div className="pointer-events-none absolute inset-y-0 w-0.5 bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.4)]" style={{ left: `${pos}%` }}>
        <span className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[10px] font-bold shadow-lg" style={{ color: "var(--camel)" }}>⇄</span>
      </div>
      <span className="absolute left-3 top-3 rounded-full px-3 py-1 text-[10px] font-bold uppercase text-white backdrop-blur-sm" style={{ background: "rgba(44,39,37,0.55)", letterSpacing: "0.08em" }}>{x.mirrorBefore}</span>
      <span className="absolute right-3 top-3 rounded-full px-3 py-1 text-[10px] font-bold uppercase text-white" style={{ background: "var(--gold)", letterSpacing: "0.08em" }}>{x.mirrorAfter}</span>
      <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))} aria-label="Before / After" data-testid="plus-mirror-range" className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0" />
    </div>
  );
};

const scrollToId = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function MiraPlusPage() {
  const { lang } = useLang();
  const x = T[lang] || T.de;

  useEffect(() => {
    const timer = setTimeout(() => {
      document.title = x.metaTitle;
      document.querySelector('meta[name="description"]')?.setAttribute("content", x.metaDesc);
    }, 0);
    document.getElementById("ld-faq-plus")?.remove();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "ld-faq-plus";
    ld.text = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: x.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) });
    document.head.appendChild(ld);
    document.getElementById("ld-offer-plus")?.remove();
    const ldOffer = document.createElement("script");
    ldOffer.type = "application/ld+json";
    ldOffer.id = "ld-offer-plus";
    ldOffer.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Mira Mirror – AI Style Coach",
      applicationCategory: "LifestyleApplication",
      operatingSystem: "iOS, Android",
      url: "https://mira-mirror.ai/mira-plus",
      publisher: { "@type": "Organization", name: "MYSYMP AG", url: "https://mira-mirror.ai" },
      offers: [
        { "@type": "Offer", name: "Mira Free", price: "0", priceCurrency: "CHF", description: "Free forever with basic limits" },
        { "@type": "Offer", name: "Mira Plus monthly", price: "6.90", priceCurrency: "CHF", description: "Mira Plus subscription, per month, cancel anytime" },
        { "@type": "Offer", name: "Mira Plus yearly", price: "59", priceCurrency: "CHF", description: "Mira Plus subscription, per year (about 29% savings)" },
      ],
    });
    document.head.appendChild(ldOffer);
    return () => { clearTimeout(timer); document.getElementById("ld-faq-plus")?.remove(); document.getElementById("ld-offer-plus")?.remove(); };
  }, [x]);

  return (
    <main style={{ background: "var(--surface)", color: "var(--on-surface)" }} className="min-h-screen">
      <Navbar />
      <div data-testid="mira-plus-page">

        {/* Hero */}
        <section className="mx-auto max-w-7xl px-5 pt-32 pb-16 sm:px-8 sm:pt-40">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <p className="flex items-center gap-2 text-[11px] font-black uppercase" style={{ letterSpacing: "0.24em", color: "var(--camel)" }}>
                <Star size={11} /> {x.kicker}
              </p>
              <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight" data-testid="plus-hero-title">{x.h1}</h1>
              <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>{x.sub}</p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button onClick={() => scrollToId("plus-preise")} data-testid="plus-hero-cta" className="pill-btn rounded-full px-8 py-3.5 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.03]" style={{ background: "var(--camel)" }}>
                  {x.cta1}
                </button>
                <button onClick={() => scrollToId("plus-vorteile")} data-testid="plus-hero-secondary" className="gold-underline text-sm font-medium" style={{ color: "var(--camel)" }}>
                  {x.cta2} →
                </button>
              </div>
              <div className="mt-8"><StoreBadges height={44} deepLink="miramirror://mira-plus" /></div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden rounded-[2rem] border-2 shadow-2xl" style={{ borderColor: "var(--line)" }}>
                <img src="/img/mira-plus-hero.jpg" alt={x.heroAlt} className="w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </section>

        {/* Pain */}
        <section className="py-16 sm:py-20" style={{ background: "var(--surface-secondary)" }}>
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal><h2 className="font-serif-display text-3xl sm:text-4xl font-semibold">{x.painTitle}</h2></Reveal>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {x.pains.map((p, i) => (
                <Reveal key={i} delay={i * 0.06} className="flex items-start gap-3 rounded-2xl border p-5" style={{ background: "var(--surface)", borderColor: "var(--line)" }} data-testid={`plus-pain-${i + 1}`}>
                  <XCircle size={18} className="mt-0.5 shrink-0" style={{ color: "var(--rose, #b56576)" }} />
                  <p className="text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{p}</p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-2xl text-base font-semibold" style={{ color: "var(--camel)" }}>{x.solution}</p>
            </Reveal>
          </div>
        </section>

        {/* 3 Schritte */}
        <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
          <Reveal><h2 className="font-serif-display text-3xl sm:text-4xl font-semibold">{x.howTitle}</h2></Reveal>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {x.steps.map((s, i) => (
              <Reveal key={i} delay={i * 0.07} className="rounded-2xl border p-6" style={{ background: "var(--surface)", borderColor: "var(--line)" }} data-testid={`plus-step-${i + 1}`}>
                <span className="font-serif-display text-4xl font-semibold" style={{ color: "var(--gold)" }}>{i + 1}</span>
                <h3 className="mt-3 text-base font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{s.d}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Must-Piece Spotlight */}
        <section className="py-16 sm:py-24" style={{ background: "var(--ink, #2C2725)", color: "#FCFAF8" }}>
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal>
              <p className="flex items-center gap-2.5 text-[11px] font-black uppercase" style={{ letterSpacing: "0.24em", color: "var(--gold)" }}>
                <span className="rounded-full px-2.5 py-1 text-[10px] font-black text-white" style={{ background: "var(--gold)", letterSpacing: "0.1em" }}>{x.mustBadge}</span>
                <Star size={11} /> {x.mustKicker}
              </p>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight" data-testid="plus-mustpiece-title">{x.mustTitle}</h2>
              <p className="mt-4 max-w-2xl text-base md:text-lg leading-relaxed opacity-85">{x.mustSub}</p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {x.mustSteps.map((s, i) => (
                <Reveal key={i} delay={i * 0.06} className="rounded-2xl border p-5" style={{ background: "rgba(252,250,248,0.06)", borderColor: "rgba(252,250,248,0.15)" }} data-testid={`plus-mustpiece-step-${i + 1}`}>
                  <span className="font-serif-display text-3xl font-semibold" style={{ color: "var(--gold)" }}>{i + 1}</span>
                  <h3 className="mt-2.5 text-sm font-semibold">{s.t}</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed opacity-75">{s.d}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Zauberspiegel Demo */}
        <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,400px)_1fr] lg:gap-16">
            <Reveal className="mx-auto w-full max-w-[360px]">
              <BeforeAfter x={x} />
              <p className="mt-3 text-center text-xs" style={{ color: "var(--muted-warm)" }}>{x.mirrorHint}</p>
            </Reveal>
            <Reveal>
              <p className="flex items-center gap-2 text-[11px] font-black uppercase" style={{ letterSpacing: "0.24em", color: "var(--camel)" }}>
                <Star size={11} /> {x.mirrorKicker}
              </p>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl font-semibold leading-tight">{x.mirrorTitle}</h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed" style={{ color: "var(--muted-warm)" }}>{x.mirrorText}</p>
              <p className="mt-5 text-sm font-semibold" style={{ color: "var(--camel)" }}>{x.mirrorQuote}</p>
            </Reveal>
          </div>
        </section>

        {/* Vorteile Grid */}
        <section id="plus-vorteile" className="py-16 sm:py-20" style={{ background: "var(--surface-secondary)" }}>
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Reveal><h2 className="font-serif-display text-3xl sm:text-4xl font-semibold">{x.benefitsTitle}</h2></Reveal>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {x.features.map((f, i) => {
                const Icon = FEATURE_ICONS[i];
                return (
                  <Reveal key={i} delay={(i % 3) * 0.06} className="rounded-2xl border p-6" style={{ background: "var(--surface)", borderColor: "var(--line)" }} data-testid={`plus-feature-${i + 1}`}>
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: "var(--rose-soft)" }}>
                      <Icon size={18} strokeWidth={1.7} style={{ color: "var(--camel)" }} />
                    </span>
                    <h3 className="mt-4 text-sm font-semibold">{f.t}</h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed" style={{ color: "var(--muted-warm)" }}>{f.d}</p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Free vs Plus */}
        <section className="mx-auto max-w-4xl px-5 py-16 sm:px-8 sm:py-20">
          <Reveal><h2 className="text-center font-serif-display text-3xl sm:text-4xl font-semibold">{x.compareTitle}</h2></Reveal>
          <Reveal delay={0.08} className="mt-8 overflow-hidden rounded-3xl border" style={{ borderColor: "var(--line)", background: "var(--surface)" }}>
            <div className="grid grid-cols-[1.3fr_0.9fr_1.1fr] items-center border-b px-5 py-4 sm:px-7" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }}>
              <span className="text-xs font-bold uppercase" style={{ letterSpacing: "0.14em", color: "var(--muted-warm)" }}>{x.compareCols[0]}</span>
              <span className="text-center text-sm font-bold">{x.compareCols[1]}</span>
              <span className="text-center text-sm font-bold" style={{ color: "var(--camel)" }}>{x.compareCols[2]}</span>
            </div>
            {x.compare.map((row, i) => (
              <div key={i} className="grid grid-cols-[1.3fr_0.9fr_1.1fr] items-center px-5 py-3.5 text-sm sm:px-7" style={{ borderTop: i === 0 ? "none" : "1px solid var(--line)" }} data-testid={`plus-compare-row-${i + 1}`}>
                <span className="font-medium">{row.l}</span>
                <span className="text-center text-[13px]" style={{ color: "var(--muted-warm)" }}>{row.f === "–" ? <Minus size={14} className="mx-auto opacity-50" /> : row.f}</span>
                <span className="flex items-center justify-center gap-1.5 text-center text-[13px] font-semibold" style={{ color: "var(--camel)" }}><Check size={13} className="shrink-0" />{row.p}</span>
              </div>
            ))}
          </Reveal>
        </section>

        {/* Preise */}
        <section id="plus-preise" className="py-16 sm:py-20" style={{ background: "var(--surface-secondary)" }}>
          <div className="mx-auto max-w-4xl px-5 sm:px-8">
            <Reveal><h2 className="text-center font-serif-display text-3xl sm:text-4xl font-semibold">{x.priceTitle}</h2></Reveal>
            <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Reveal className="rounded-3xl border p-7" style={{ background: "var(--surface)", borderColor: "var(--line)" }} data-testid="plus-price-monthly">
                <p className="text-xs font-bold uppercase" style={{ letterSpacing: "0.14em", color: "var(--muted-warm)" }}>{x.monthlyLabel}</p>
                <p className="mt-3 font-serif-display text-4xl font-semibold">CHF 6.90<span className="text-base font-normal" style={{ color: "var(--muted-warm)" }}>{x.perMonth}</span></p>
                <ul className="mt-5 space-y-2 text-sm" style={{ color: "var(--muted-warm)" }}>
                  {x.priceBullets.map((b, i) => (<li key={i} className="flex gap-2"><Check size={15} style={{ color: "var(--camel)" }} /> {b}</li>))}
                </ul>
              </Reveal>
              <Reveal delay={0.07} className="relative rounded-3xl border-2 p-7" style={{ background: "var(--surface)", borderColor: "var(--gold)" }} data-testid="plus-price-yearly">
                <span className="absolute -top-3 right-6 rounded-full px-3 py-1 text-[10px] font-bold uppercase text-white" style={{ background: "var(--gold)", letterSpacing: "0.1em" }}>{x.saveBadge}</span>
                <p className="text-xs font-bold uppercase" style={{ letterSpacing: "0.14em", color: "var(--camel)" }}>{x.yearlyLabel}</p>
                <p className="mt-3 font-serif-display text-4xl font-semibold">CHF 59<span className="text-base font-normal" style={{ color: "var(--muted-warm)" }}>{x.perYear}</span></p>
                <p className="mt-1 text-xs" style={{ color: "var(--muted-warm)" }}>{x.yearlyNote}</p>
                <ul className="mt-5 space-y-2 text-sm" style={{ color: "var(--muted-warm)" }}>
                  {x.priceBullets.map((b, i) => (<li key={i} className="flex gap-2"><Check size={15} style={{ color: "var(--camel)" }} /> {b}</li>))}
                </ul>
              </Reveal>
            </div>
            <Reveal delay={0.12}>
              <p className="mx-auto mt-7 max-w-2xl rounded-2xl border px-5 py-4 text-center text-sm leading-relaxed" style={{ borderColor: "var(--line)", background: "var(--surface)", color: "var(--muted-warm)" }} data-testid="plus-fairuse">
                ✦ {x.fairuse[0]}<strong>{x.fairuse[1]}</strong>{x.fairuse[2]}<strong>{x.fairuse[3]}</strong>{x.fairuse[4]}
              </p>
            </Reveal>
          </div>
        </section>

        {/* Testimonials + Trust */}
        <section className="mx-auto max-w-5xl px-5 py-16 sm:px-8 sm:py-20">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {x.quotes.map((q, i) => (
              <Reveal key={i} delay={i * 0.07} className="rounded-3xl border p-7" style={{ background: "var(--surface)", borderColor: "var(--line)" }} data-testid={`plus-quote-${i + 1}`}>
                <p className="font-serif-display text-xl leading-snug">«{q.q}»</p>
                <p className="mt-4 text-sm font-semibold" style={{ color: "var(--camel)" }}>{q.n}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.12} className="mt-9 flex flex-wrap items-center justify-center gap-5">
            <StoreBadges height={44} deepLink="miramirror://mira-plus" />
          </Reveal>
          <Reveal delay={0.15} className="mt-5 flex flex-wrap items-center justify-center gap-5 text-xs" style={{ color: "var(--muted-warm)" }}>
            <span className="flex items-center gap-1.5"><Check size={13} style={{ color: "var(--camel)" }} /> {x.trust[0]}</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={13} style={{ color: "var(--camel)" }} /> {x.trust[1]}</span>
            <span className="flex items-center gap-1.5"><ListChecks size={13} style={{ color: "var(--camel)" }} /> {x.trust[2]}</span>
          </Reveal>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-20">
          <Reveal><h2 className="text-center font-serif-display text-3xl sm:text-4xl font-semibold">{x.faqTitle}</h2></Reveal>
          <Reveal delay={0.08} className="mt-8">
            <Accordion type="single" collapsible className="w-full" data-testid="plus-faq">
              {x.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-semibold">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </section>

        {/* Abschluss-CTA */}
        <section className="py-16 sm:py-24" style={{ background: "var(--ink, #2C2725)", color: "#FCFAF8" }}>
          <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
            <Reveal>
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">{x.ctaTitle}</h2>
              <p className="mx-auto mt-4 max-w-xl text-base opacity-80">{x.ctaSub}</p>
              <div className="mt-8 flex justify-center"><StoreBadges height={48} deepLink="miramirror://mira-plus" /></div>
              <p className="mt-6 text-xs opacity-60">{x.ctaNote}</p>
            </Reveal>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
}
